import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isElementNode,
  $createParagraphNode,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
} from "lexical";
import type { ElementFormatType, LexicalCommand, TextFormatType } from "lexical";
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  ListNode,
} from "@lexical/list";
import { $setBlocksType } from "@lexical/selection";
import { $createHeadingNode, $isHeadingNode } from "@lexical/rich-text";
import { $getNearestBlockElementAncestorOrThrow, $getNearestNodeOfType } from "@lexical/utils";
import { Fragment, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { IconCaret } from "../../../../../common/icons/IconCaret/IconCaret";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../../../content/downlineProfile";
import {
  IconAlignCenter,
  IconAlignLeft,
  IconAlignRight,
  IconBold,
  IconBulletedList,
  IconItalic,
  IconNumberedList,
  IconParagraph,
  IconRedo,
  IconStrikethrough,
  IconSubscript,
  IconSuperscript,
  IconUnderline,
  IconUndo,
} from "./icons";
import "./NotesToolBar.scss";

type BlockType = "paragraph" | "h1" | "h2" | "h3";
type TextStyle = Partial<Record<TextFormatType, boolean>>;

const BLOCK_TYPE_OPTIONS: { value: BlockType; contentKey: string }[] = [
  { value: "paragraph", contentKey: "normalText" },
  { value: "h1", contentKey: "heading1" },
  { value: "h2", contentKey: "heading2" },
  { value: "h3", contentKey: "heading3" },
];

// A command button that dispatches a Lexical command with an optional payload.
// `onMouseDown` (with preventDefault) keeps the editor selection intact.
const COMMAND_BUTTONS: {
  contentKey: string;
  Icon: () => ReactNode;
  command: LexicalCommand<unknown>;
  payload?: unknown;
}[] = [
  { contentKey: "undo", Icon: IconUndo, command: UNDO_COMMAND },
  { contentKey: "redo", Icon: IconRedo, command: REDO_COMMAND },
];

const TEXT_FORMAT_BUTTONS: {
  contentKey: string;
  Icon: () => ReactNode;
  format: TextFormatType;
  breakAfter?: boolean;
}[] = [
  { contentKey: "bold", Icon: IconBold, format: "bold" },
  { contentKey: "italic", Icon: IconItalic, format: "italic" },
  { contentKey: "strikethrough", Icon: IconStrikethrough, format: "strikethrough" },
  { contentKey: "underline", Icon: IconUnderline, format: "underline", breakAfter: true },
  { contentKey: "superscript", Icon: IconSuperscript, format: "superscript" },
  { contentKey: "subscript", Icon: IconSubscript, format: "subscript" },
];

const ALIGNMENT_BUTTONS: {
  contentKey: string;
  Icon: () => ReactNode;
  value: ElementFormatType;
}[] = [
  { contentKey: "alignLeft", Icon: IconAlignLeft, value: "left" },
  { contentKey: "alignCenter", Icon: IconAlignCenter, value: "center" },
  { contentKey: "alignRight", Icon: IconAlignRight, value: "right" },
];

const LIST_BUTTONS: {
  contentKey: string;
  Icon: () => ReactNode;
  insertCommand: LexicalCommand<undefined>;
  listType: "bullet" | "number";
}[] = [
  {
    contentKey: "bulletedList",
    Icon: IconBulletedList,
    insertCommand: INSERT_UNORDERED_LIST_COMMAND,
    listType: "bullet",
  },
  {
    contentKey: "numberedList",
    Icon: IconNumberedList,
    insertCommand: INSERT_ORDERED_LIST_COMMAND,
    listType: "number",
  },
];

const ToolBarButton = ({
  label,
  pressed,
  onActivate,
  children,
}: {
  label: string;
  pressed?: boolean;
  onActivate: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    className="NotesToolBar__option"
    aria-label={label}
    aria-pressed={pressed}
    onMouseDown={(e) => {
      e.preventDefault();
      onActivate();
    }}
  >
    {children}
  </button>
);

export const NotesToolBar = () => {
  const { t } = useTranslation(["downlineProfile"]);
  const [editor] = useLexicalComposerContext();
  const [textStyle, setTextStyle] = useState<TextStyle>({});
  const [alignment, setAlignment] = useState<ElementFormatType>("");
  const [blockType, setBlockType] = useState<BlockType>("paragraph");
  const [listType, setListType] = useState<string | null>(null);
  const [isBlockTypeOpen, setIsBlockTypeOpen] = useState(false);
  const blockTypeRef = useRef<HTMLDivElement>(null);

  const label = (contentKey: string) =>
    t(
      `notes.editor.${contentKey}`,
      defaultContent.notes.editor[contentKey as keyof typeof defaultContent.notes.editor] as string,
    );

  useEffect(() => {
    if (!isBlockTypeOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (blockTypeRef.current && !blockTypeRef.current.contains(event.target as Node)) {
        setIsBlockTypeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isBlockTypeOpen]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        setTextStyle({
          bold: selection.hasFormat("bold"),
          italic: selection.hasFormat("italic"),
          underline: selection.hasFormat("underline"),
          superscript: selection.hasFormat("superscript"),
          subscript: selection.hasFormat("subscript"),
          strikethrough: selection.hasFormat("strikethrough"),
        });

        const anchorNode = selection.anchor.getNode();
        const element = $isElementNode(anchorNode) ? anchorNode : $getNearestBlockElementAncestorOrThrow(anchorNode);
        setAlignment(element.getFormatType());
        const tag = $isHeadingNode(element) ? element.getTag() : "paragraph";
        setBlockType(tag === "h1" || tag === "h2" || tag === "h3" ? tag : "paragraph");

        const listNode = $getNearestNodeOfType(anchorNode, ListNode);
        setListType(listNode ? listNode.getListType() : null);
      });
    });
  }, [editor]);

  const applyBlockType = (type: BlockType) => {
    editor.update(() => {
      let selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        $getRoot().selectEnd();
        selection = $getSelection();
      }
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => (type === "paragraph" ? $createParagraphNode() : $createHeadingNode(type)));
      }
    });
    setBlockType(type);
  };

  const blockTypeLabel = BLOCK_TYPE_OPTIONS.find((option) => option.value === blockType)?.contentKey;

  return (
    <div className="NotesToolBar">
      {/* HISTORY ACTIONS */}
      {COMMAND_BUTTONS.map(({ contentKey, Icon, command, payload }) => (
        <ToolBarButton
          key={contentKey}
          label={label(contentKey)}
          onActivate={() => editor.dispatchCommand(command, payload)}
        >
          <Icon />
        </ToolBarButton>
      ))}

      <div className="NotesToolBar__section-divider"></div>

      {/* BLOCK TYPE */}
      <div className="NotesToolBar__block-type" ref={blockTypeRef}>
        <button
          type="button"
          className="NotesToolBar__block-type-trigger"
          aria-haspopup="menu"
          aria-expanded={isBlockTypeOpen}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setIsBlockTypeOpen((open) => !open)}
        >
          <span className="NotesToolBar__block-type-label">{blockTypeLabel ? label(blockTypeLabel) : ""}</span>
          <IconCaret direction={isBlockTypeOpen ? "up" : "down"} size="xs" />
        </button>

        {isBlockTypeOpen && (
          <div className="NotesToolBar__block-type-menu" role="menu">
            {BLOCK_TYPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={blockType === option.value}
                className="NotesToolBar__block-type-item"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  applyBlockType(option.value);
                  setIsBlockTypeOpen(false);
                }}
              >
                <span className="NotesToolBar__block-type-row">
                  <span className="NotesToolBar__block-type-option">
                    {option.value === "paragraph" ? <IconParagraph /> : option.value.toUpperCase()}
                  </span>
                  {label(option.contentKey)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="NotesToolBar__section-divider"></div>

      {/* STYLING */}
      {TEXT_FORMAT_BUTTONS.map(({ contentKey, Icon, format, breakAfter }) => (
        <Fragment key={contentKey}>
          <ToolBarButton
            label={label(contentKey)}
            pressed={textStyle[format]}
            onActivate={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)}
          >
            <Icon />
          </ToolBarButton>
          {breakAfter && <div className="NotesToolBar__break" aria-hidden="true"></div>}
        </Fragment>
      ))}

      <div className="NotesToolBar__section-divider"></div>

      {/* ALIGNMENT */}
      {ALIGNMENT_BUTTONS.map(({ contentKey, Icon, value }) => (
        <ToolBarButton
          key={contentKey}
          label={label(contentKey)}
          pressed={value === "left" ? alignment === "left" || alignment === "" : alignment === value}
          onActivate={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, value)}
        >
          <Icon />
        </ToolBarButton>
      ))}

      <div className="NotesToolBar__section-divider"></div>

      {/* LIST */}
      {LIST_BUTTONS.map(({ contentKey, Icon, insertCommand, listType: buttonListType }) => {
        const isActive = listType === buttonListType;
        return (
          <ToolBarButton
            key={contentKey}
            label={label(contentKey)}
            pressed={isActive}
            // Clicking the active list type toggles it back to normal text.
            onActivate={() => editor.dispatchCommand(isActive ? REMOVE_LIST_COMMAND : insertCommand, undefined)}
          >
            <Icon />
          </ToolBarButton>
        );
      })}
    </div>
  );
};
